import { type FC, type FormEventHandler } from 'react';
import {
    Avatar,
    Badge,
    Button,
    Fieldset,
    Flex,
    Group,
    Loader,
    MultiSelect,
    type MultiSelectProps,
    NumberInput,
    Paper,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { type UseFormReturnType } from '@mantine/form';
import { type PayoutGroupTrader } from '~/entities/payout';
import { defaultOptionsFilter } from '~/shared/lib/utils/defaultOptionsFilter';

interface CascadeGroupEditorFormValues {
    name: string;
    capacity: number;
    overflowCapacity: number;
    traderIds: string[];
}

interface CascadeGroupInfoCardProps {
    form: UseFormReturnType<CascadeGroupEditorFormValues>;
    tradersOptions: { value: string; label: string }[];
    isTradersLoading: boolean;
    currentTraders?: PayoutGroupTrader[];
    isSubmitPending: boolean;
    onSubmit: FormEventHandler<HTMLFormElement>;
}

const renderTraderSelectOption: MultiSelectProps['renderOption'] = ({ option }) => (
    <Group gap='sm'>
        <Avatar
            name={option.label}
            size={36}
            radius='xl'
        />
        <div>
            <Text size='sm'>{option.label}</Text>
            <Text
                size='xs'
                opacity={0.5}
            >
                {option.value}
            </Text>
        </div>
    </Group>
);

export const CascadeGroupInfoCard: FC<CascadeGroupInfoCardProps> = ({
    form,
    tradersOptions,
    isTradersLoading,
    currentTraders,
    isSubmitPending,
    onSubmit,
}) => {
    return (
        <Paper
            p='md'
            radius='md'
            withBorder
        >
            <form onSubmit={onSubmit}>
                <Stack gap='md'>
                    <TextInput
                        label='Название'
                        placeholder='Введите название'
                        {...form.getInputProps('name')}
                    />

                    <Fieldset legend='Очередь'>
                        <Flex
                            gap='md'
                            wrap='wrap'
                        >
                            <NumberInput
                                flex={1}
                                miw={200}
                                min={0}
                                label='Вместимость'
                                placeholder='Введите число'
                                {...form.getInputProps('capacity')}
                            />

                            <NumberInput
                                flex={1}
                                miw={200}
                                min={0}
                                label='Дополнительная вместимость'
                                placeholder='Введите число'
                                {...form.getInputProps('overflowCapacity')}
                            />
                        </Flex>
                    </Fieldset>

                    {currentTraders !== undefined && (
                        <Stack gap='xs'>
                            <Text
                                fw={600}
                                size='sm'
                            >
                                Текущие трейдеры
                            </Text>

                            {currentTraders.length ? (
                                <Group
                                    gap='xs'
                                    wrap='wrap'
                                >
                                    {currentTraders.map((trader) => (
                                        <Badge
                                            key={trader.id?.value}
                                            variant='light'
                                            radius='xl'
                                        >
                                            {trader.traderName?.value || trader.id?.value}
                                        </Badge>
                                    ))}
                                </Group>
                            ) : (
                                <Text
                                    size='sm'
                                    c='dimmed'
                                >
                                    Нет трейдеров
                                </Text>
                            )}
                        </Stack>
                    )}

                    <MultiSelect
                        searchable
                        label='Трейдеры'
                        placeholder='Выберите трейдеров'
                        data={tradersOptions}
                        readOnly={isTradersLoading}
                        filter={defaultOptionsFilter}
                        nothingFoundMessage='Нет трейдеров'
                        renderOption={renderTraderSelectOption}
                        leftSection={isTradersLoading ? <Loader size='xs' /> : null}
                        {...form.getInputProps('traderIds')}
                    />

                    <Button
                        type='submit'
                        loading={isSubmitPending}
                        disabled={isSubmitPending || !form.isValid() || isTradersLoading}
                    >
                        Сохранить
                    </Button>
                </Stack>
            </form>
        </Paper>
    );
};
